import React, { useEffect, useState } from "react";
import moment from "moment";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { Select as SelectNew, Select } from "components/select/";
import { useCustomerContext } from "../../context/CustomerContext";
import { GetAccountScheduledPayments, PutAccountScheduledPayment } from "api/rpc/2024-04/facilityAdmin/client/accounts";
import { capitalize, formatDate } from "helpers/Helpers";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { showError, showSuccess } from "redux/actions/ui";
import Callout from "components/callout/Callout";
import { useTranslation } from "react-i18next";

type AccountScheduledPaymentProps = {
  open: boolean;
  onClose: () => void;
  onOk: () => Promise<void>;
  scheduledPayment: IScheduledPayment;
};

interface IScheduledPayment {
  id: number;
  amount: string;
  process_date: Date;
  payment_method_id: number;
  charge_balance: boolean;
  note: string;
}

export default function EditAccountScheduledPaymentModal(props: AccountScheduledPaymentProps) {
  const { open, onClose, onOk, scheduledPayment } = props;
  const { state, updateState } = useCustomerContext();
  const dispatch = useAppDispatch();
  const { Option } = SelectNew;
  const { t } = useTranslation();

  const [editScheduledPayment, setEditScheduledPayment] = useState<IScheduledPayment>({
    id: null,
    amount: null,
    process_date: null,
    payment_method_id: null,
    charge_balance: null,
    note: null,
  });

  useEffect(() => {
    if (open && state?.paymentMethods && state?.paymentMethods?.length > 0) {
      setEditScheduledPayment(prevState => ({ ...prevState, payment_method_id: state?.paymentMethods[0]?.id }));
    }
  }, [state?.paymentMethods, open]);

  useEffect(() => {
    setEditScheduledPayment(prevState => ({
      ...prevState,
      id: scheduledPayment?.id,
      amount: scheduledPayment?.amount,
      process_date: moment(scheduledPayment?.process_date, "YYYY-MM-DD").toDate(),
      payment_method_id: scheduledPayment?.payment_method_id,
      charge_balance: scheduledPayment?.charge_balance,
      note: scheduledPayment?.note,
    }));
  }, [open]);

  function handleDateChange(date: Date) {
    setEditScheduledPayment(prevState => ({ ...prevState, process_date: date }));
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setEditScheduledPayment(prevState => ({ ...prevState, [id]: value }));
  }

  function handlePaymentMethodChange(id: number) {
    setEditScheduledPayment(prevState => ({ ...prevState, payment_method_id: id }));
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    setEditScheduledPayment(prevState => ({
      ...prevState,
      [id]: checked,
      amount: checked ? "0" : prevState?.amount,
    }));
  }

  function handleOnClose() {
    setEditScheduledPayment(prevState => ({
      ...prevState,
      amount: "0",
      process_date: new Date(),
      paymen_method_id: null,
      charge_balance: false,
      note: "",
    }));
    void onClose();
  }

  async function updateScheduledPayment() {
    // Check if date is in the future
    const now = new Date();
    if (editScheduledPayment?.process_date <= now) {
      dispatch(showError(t("Payment date must be in the future")));
      return;
    }

    const res = await PutAccountScheduledPayment(
      {
        id: editScheduledPayment?.id,
        payment_method_id: editScheduledPayment?.payment_method_id,
        amount: Number(editScheduledPayment?.amount),
        process_date: formatDate(editScheduledPayment?.process_date),
        charge_balance: editScheduledPayment?.charge_balance,
        note: editScheduledPayment?.note,
      },
      true,
    );
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating scheduled payment")));
      return;
    }
    dispatch(showSuccess(t("Successfully created scheduled payment")));
    void handleOnClose();
    await onOk();
  }

  const disableOK =
    editScheduledPayment.amount === "" ||
    isNaN(Number(editScheduledPayment.amount)) ||
    !editScheduledPayment.process_date ||
    !editScheduledPayment.payment_method_id;

  return (
    <Sheet
      open={open}
      size="small"
      height="flexible"
      closable
      title={t("Edit Scheduled Payment")}
      onCancel={handleOnClose}
      onOk={updateScheduledPayment}
      cancelText={t("Cancel")}
      okText={t("Update")}
      overflow
      okDisabled={disableOK}
    >
      {state?.paymentMethods?.length === 0 ? (
        <Callout
          type="error"
          title={t("No Saved Payment Methods")}
          content={t("This customer does not have any payment methods saved. Add a new payment method to create a scheduled payment.")}
        />
      ) : (
        <Form>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Amount")}
                type="number"
                id="amount"
                defaultValue={editScheduledPayment.amount}
                value={editScheduledPayment.amount}
                onChange={handleInputChange}
                disabled={editScheduledPayment?.charge_balance}
              />
              <DatePickerInput
                months={1}
                position="left"
                startingDate={editScheduledPayment.process_date}
                setStartingDate={handleDateChange}
                label={t("Payment Date")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label={t("Payment Method")}
                onChange={handlePaymentMethodChange}
                defaultValue={editScheduledPayment.payment_method_id}
              >
                {state?.paymentMethods?.length > 0 &&
                  state?.paymentMethods?.map((card, index) => {
                    return (
                      <Option key={index} value={card?.id}>
                        {capitalize(card?.brand)} •••• •••• •••• {card?.last4}
                      </Option>
                    );
                  })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("Note")}
                id="note"
                value={editScheduledPayment.note}
                onChange={handleInputChange}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                size="medium"
                value={editScheduledPayment.charge_balance}
                checked={editScheduledPayment.charge_balance}
                onChange={handleCheckboxChange}
                label={t("Charge full statement balance")}
                id="charge_balance"
              />
            </FormLayout.Group>
          </FormLayout>
        </Form>
      )}
    </Sheet>
  );
}
